import { IconButton, Tooltip } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import {
  Block as BlockIcon,
  MoreHoriz as MoreHorizIcon,
  Search as PageviewIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
} from '@material-ui/icons';
import { isEmpty, isFunction } from 'lodash';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';
import QCXPopupState from '../../../../../components/popup-state/QCXPopupState';
import { setQuery } from '../../../../../features/controller/controllerSlice';
import QCXConfirmDialog from '../../../../../shared-components/dialog/QCXConfirmDialog';
import QCXListItemIcon from '../../../../../shared-components/list-item-icon/QCXListItemIcon';
import SimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import { formatDate } from '../../../../../utils/general/general-utils';
import { formatCnpj } from '../../../../../utils/hooks/form/field/formatters';
import useOperationMiddleware from '../../../../../utils/hooks/operation/middleware/useOperationMiddleware';
import { isLoadingStatus, isRefresh } from '../../../../../utils/store/store-utils';
import {
  selectDadosEmbarqueImpCount,
  selectDadosEmbarques,
  selectHistoryModel,
  selectRefresh,
  selectStatus,
} from '../api/dadosEmbarqueSelectors';
import {
  changeToConsultMode,
  resetBackgroundMode,
  resetMode,
  resetModel,
  resetRefresh,
  resetSubMode,
  loading,
  success,
  failure,
  refresh as refreshAction,
} from '../api/dadosEmbarqueSlice';
import { generateReportById } from '../api/dadosEmbarqueAPI';
import {
  activateByIdAsync,
  fetchAllDadosEmbarqueImportacaoByFilterAsync,
  inactivateByIdAsync,
} from '../api/dadosEmbarqueThunks';
import { convertUTCStringToLocalDateTime } from '../../../../../utils/general/dateTime-utils';
import useGenericDownloader from '../../../../../utils/hooks/downloader/useGenericDownloader';
import { setErrorFeedback, setSuccessFeedback } from '../../../../../features/feedback/feedbackSlice';
import { selectUnidadeSelecionada } from '../../../../../features/usuario-logado/usuarioLogadoSlice';

export default function DadosEmbarqueConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useDispatch();

  const refresh = useSelector(selectRefresh);
  const dadosEmbarques = useSelector(selectDadosEmbarques);
  const historyModel = useSelector(selectHistoryModel);
  const status = useSelector(selectStatus);
  const genericDownloader = useGenericDownloader({ autoDownload: true });
  const tenant = useSelector(selectUnidadeSelecionada);
  // const isVisao = tenant === 3491664


  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const fetchAllFirstPage = useCallback(() => {
    dispatch(
      fetchAllDadosEmbarqueImportacaoByFilterAsync([
        { name: 'page', value: 1 },
        { name: 'size', value: 10 },
      ])
    );
  }, []);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
    dispatch(resetSubMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, []);

  useEffect(() => {
    resetModes();
    resetModels();
  }, []);

  useEffect(() => {
    const checkIfIsRefresh = () => {
      if (isRefresh(refresh)) {
        fetchAllFirstPage();
        dispatch(resetRefresh());
      }
    };

    checkIfIsRefresh();
  }, [refresh, fetchAllFirstPage]);

  useEffect(() => {
    const checkIfExistsQuery = () => {
      if (!isEmpty(historyModel) && historyModel?.last?.numero) {
        dispatch(setQuery(historyModel?.last?.numero));
      }
    };

    checkIfExistsQuery();
  }, [historyModel]);

  const handleImprimirByIdRelatorio = async (data, callback = undefined) => {
    const fetcher = async () => {
      dispatch(loading());

      const query = [
        { name: 'unidadeNegocioId', value: tenant }
      ]; 

      const response = await generateReportById(data?.id, query);

      return response;
    };

    const handlers = {
      onSuccess: () => {
        dispatch(success());

        dispatch(
          setSuccessFeedback({
            message: 'Relatório emitido com sucesso !',
          })
        );

        dispatch(refreshAction());
        if (data?.meta?.numero) dispatch(setQuery(data?.meta?.numero));

        if (typeof callback === 'function') callback();
      },
      onNoContent: () => {
        dispatch(failure());

        dispatch(
          setErrorFeedback({
            message: 'Não foi possivel gerar o relatório.',
          })
        );
      },
      onError: (reason) => {
        dispatch(failure());

        const erro = Array.isArray(reason) ? reason[0] : reason;

        let msg = erro?.message || 'Erro ao tentar gerar o relatório.';
        const dataError = erro?.response?.data;

        try {
          if (dataError instanceof ArrayBuffer) {
            msg =
              new TextDecoder('utf-8').decode(new Uint8Array(dataError)).trim() || msg;
          } else if (ArrayBuffer.isView(dataError)) {
            msg = new TextDecoder('utf-8').decode(dataError).trim() || msg;
          } else if (typeof dataError === 'string' && dataError.trim()) {
            msg = dataError.trim();
          } else if (dataError && typeof dataError === 'object' && dataError.message) {
            msg = dataError.message;
          }
        } catch (e) {
          // mantém msg padrão
        }

        dispatch(setErrorFeedback({ message: msg }));
      },
    };

    genericDownloader.downloadFileById(fetcher, handlers);
  };

  const [handleImprimirByIdMiddleware, operationOfImprimirById] = useOperationMiddleware(
    handleImprimirByIdRelatorio,
    []
  );

  const handleImprimirByIdClick = useCallback(
    (row, popupState) => {
      const numero = row?.followUpNumero || '';

      const handleImprimirById = (event) => {
        if (isFunction(event?.stopPropagation)) {
          popupState?.close?.(event);
          event.stopPropagation();

          const configureOperation = () => ({
            options: {
              title: 'Deseja emitir o Relatório ?',
              message: 'Ao confirmar será emitido o relatório.',
            },
          });

          const data = {
            id: row?.id,
            meta: { numero },
          };

          handleImprimirByIdMiddleware(configureOperation, data);
        }
      };

      return handleImprimirById;
    },
    [handleImprimirByIdMiddleware]
  );



  const handleConsultarClick = useCallback(
    (event, data) => {
      if (isFunction(event?.stopPropagation)) {
        event.stopPropagation();

        dispatch(changeToConsultMode());

        history.push(t('com.muralis.qcx.url.dadosEmbarqueDetalhes', { id: data?.id }));
      }
    },
    [history]
  );

  const [handleActivateOrInactivateByIdClickMiddleware, operationOfActivateOrInactivateById] = useOperationMiddleware(
    async (data) => {
      const isInactivation = data?.active;

      if (isInactivation) {
        dispatch(inactivateByIdAsync(data?.id));

        return;
      }

      dispatch(activateByIdAsync(data?.id));
    },
    [dadosEmbarques]
  );

  const handleConfigureActivateOrInactivateByIdClick = useCallback(
  (row, popupState) => {
    const { id, active, followUpNumero } = row;

    const handleActivateOrInactivateByIdClick = (event) => {
      if (isFunction(event?.stopPropagation)) {
        popupState.close(event);

        event.stopPropagation();

        const configureOperation = () => ({
          options: {
            title: t('com.muralis.qcx.acoes.confirmarOperacaoExclusao').toUpperCase(),
            message: t('com.muralis.qcx.acoes.confirmacaoAcaoDI', {
              acao: active ? t('com.muralis.qcx.acoes.excluir') : t('com.muralis.qcx.acoes.recuperar'),
              numero: followUpNumero,
            }),
          },
        });

        const dataOfOperationActivateOrInactivate = {
          id,
          active,
        };

        handleActivateOrInactivateByIdClickMiddleware(
          configureOperation,
          dataOfOperationActivateOrInactivate
        );
      }
    };

    return handleActivateOrInactivateByIdClick;
  },
  [handleActivateOrInactivateByIdClickMiddleware, t]
);

  const columns = useMemo(
    () => [
      {
        field: 'followUpNumero',
        headerName: t('com.muralis.qcx.processo'),
        headerAlign: 'center',
        align: 'center',
        flex: 140,
        valueGetter: ({ row }) => row?.followUpNumero || '-',
      },
      {
        field: 'importadorNome',
        headerName: t('com.muralis.qcx.cliente.label'),
        flex: 180,
        valueGetter: ({ row }) => row?.importadorNome || '-',
      },
      {
        field: 'importadorCnpj',
        headerName: t('com.muralis.qcx.empresa.CNPJ'),
        flex: 140,
        valueGetter: ({ row }) => formatCnpj(row?.importadorCnpj) || '-',
      },
      {
        field: 'insertionDate',
        headerName: t('com.muralis.qcx.abertura'),
        headerAlign: 'center',
        align: 'center',
        type: 'date',
        flex: 100,
        valueGetter: ({ row }) =>
          formatDate(
            convertUTCStringToLocalDateTime(row?.insertionDate),
            'DD/MM/YYYY HH:mm:ss',
            'DD/MM/YYYY'
          ) || '-',
      },
      {
        field: 'actions',
        headerName: t('com.muralis.qcx.acoes.label'),
        headerAlign: 'center',
        align: 'center',
        flex: 120,
        renderCell: ({ row }) => (
          <>
            <QCXPopupState popupId="popup-menu-follow-up">
              {(popupState) => (
                <>
                  <Tooltip title={t('com.muralis.qcx.maisOpcoes')} arrow>
                    <IconButton
                      key={`btn-more-options-${row?.id}`}
                      name={`btn-more-options-${row?.id}`}
                      {...bindTrigger(popupState)}
                    >
                      <MoreHorizIcon color="secondary" size={20} />
                    </IconButton>
                  </Tooltip>

                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      onClick={(event) => {
                        popupState.close(event);

                        handleConsultarClick(event, {
                          id: row?.id,
                        });
                      }}
                    >
                      <QCXListItemIcon>
                        <PageviewIcon fontSize="small" />
                      </QCXListItemIcon>
                      <Typography
                        variant="inherit"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {t('com.muralis.qcx.acoes.consultar').toUpperCase()}
                      </Typography>
                    </MenuItem>

                    <MenuItem onClick={handleImprimirByIdClick(row, popupState)}>
                      <QCXListItemIcon>
                        <PrintIcon fontSize="small" color="primary" />
                      </QCXListItemIcon>
                      <Typography variant="inherit" style={{ fontSize: 12 }}>
                        {t('com.muralis.qcx.acoes.imprimir').toUpperCase()}
                      </Typography>
                    </MenuItem>

                    {userHasDeleteRole(authInfo, 'importacao-follow-up') && (
                      <MenuItem
                        onClick={handleConfigureActivateOrInactivateByIdClick(row, popupState)}
                        disabled
                      >
                        <QCXListItemIcon>
                          <BlockIcon fontSize="small" color="disabled" />
                        </QCXListItemIcon>
                        <Typography
                          variant="inherit"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          {t('com.muralis.qcx.acoes.cancelar').toUpperCase()}
                        </Typography>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </QCXPopupState>
          </>
        ),
      },
    ],
    [handleConsultarClick, handleConfigureActivateOrInactivateByIdClick, handleImprimirByIdClick, t, authInfo]
  );


  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloImportacao'),
        name: t('com.muralis.qcx.importacao.label'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.dadosEmbarque.label'),
      },
      default: true,
    },
  ];

  const handleConfigChange = useCallback(({ currentPage, pageSize, filterInputValue }) => {
    dispatch(
      fetchAllDadosEmbarqueImportacaoByFilterAsync([
        { name: 'page', value: currentPage },
        { name: 'size', value: pageSize },
        { name: 'numero', value: filterInputValue },
      ])
    );
  }, []);

  const rowsCount = useSelector(selectDadosEmbarqueImpCount);

  const templateProps = useMemo(() => ({
    page: {
      title: t('com.muralis.qcx.dadosEmbarque.label'),
      icon: <ReceiptIcon />,
      breadcrumbs,
    },
    table: {
      columns,
      rowsCount,
      onConfigChangeCallback: handleConfigChange,
    },
  }));

  return (
    <SimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={dadosEmbarques}
      isLoading={isLoading}
      authInfo={authInfo}
      requiredRoles={['importacao-follow-up']}
    >
      <QCXConfirmDialog
        key="confirm-dialog-activate-inactivate-follow-up"
        id="confirm-dialog-activate-inactivate-follow-up"
        open={operationOfActivateOrInactivateById?.active}
        title={operationOfActivateOrInactivateById?.title}
        content={operationOfActivateOrInactivateById?.message}
        endContent={operationOfActivateOrInactivateById?.endMessage}
        onConfirm={operationOfActivateOrInactivateById.confirm}
        onClose={operationOfActivateOrInactivateById?.reset}
      />
      <QCXConfirmDialog
        key="confirm-dialog-imprimir-dados-embarque"
        id="confirm-dialog-imprimir-dados-embarque"
        open={operationOfImprimirById?.active}
        title={operationOfImprimirById?.title}
        content={operationOfImprimirById?.message}
        endContent={operationOfImprimirById?.endMessage}
        onConfirm={operationOfImprimirById.confirm}
        onClose={operationOfImprimirById?.reset}
      />

    </SimpleConsultPageTemplate>
  );
}
